// pages/PlayerStatsPage.jsx
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Topbar from "./Topbar";
import Footer from "./Footer";
import Loader from "./Loader";
import axios from "axios";

const apiBase = process.env.REACT_APP_API_URL;

export default function PlayerStatsPage() {
  const { playerId, leagueId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [averages, setAverages] = useState(null);
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);
  //   const [playerName, setPlayerName] = useState("");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/api/player/${playerId}/profile`);
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [playerId]);

  const fetchAverages = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/api/player/${playerId}/averages`);
      const data = res.data;
      if (data.length) {
        const totalGames = data.reduce((sum, t) => sum + Number(t.games_played), 0);
        const weightedAvg = (field) => data.reduce((sum, t) => sum + Number(t[field]) * Number(t.games_played), 0) / totalGames;

        setAverages({
          avg_rating: weightedAvg("avg_points"),
          avg_kda: weightedAvg("avg_kda"),
          avg_cspm: weightedAvg("avg_cspm"),
          avg_gpm: weightedAvg("avg_gpm"),
          avg_dpm: weightedAvg("avg_dpm"),
          game_count: totalGames,
          most_played_champions: data
            .flatMap((t) => t.most_played_champions?.split(",").map((c) => c.trim()) || [])
            .reduce((acc, champ) => {
              acc[champ] = (acc[champ] || 0) + 1;
              return acc;
            }, {}),
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [playerId]);

  const fetchTrophies = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/api/player/${playerId}/trophies`);
      setTrophies(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [playerId]);

  useEffect(() => {
    if (playerId && leagueId) {
      fetchStats();
      fetchAverages();
      fetchTrophies();
    }
  }, [playerId, leagueId, fetchStats, fetchAverages, fetchTrophies]);

  function formatDate(isoString) {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
  }

  // Group stats by formatted date
  const grouped = stats.reduce((acc, s) => {
    const key = s.match_id;
    if (!acc[key]) acc[key] = { date: formatDate(s.start_time), games: [] };
    acc[key].games.push(s);
    return acc;
  }, {});

  // Sort games within each match by game_number
  Object.values(grouped).forEach((match) => {
    match.games.sort((a, b) => a.game_number - b.game_number);
  });

  function getBadgeClass(rating) {
    if (rating >= 80) return "badge-elite";
    if (rating >= 65) return "badge-high";
    if (rating >= 50) return "badge-mid";
    if (rating >= 40) return "badge-average";
    return "badge-low";
  }

  function getBarColor(rating) {
    if (rating >= 80) return "bar-elite";
    if (rating >= 65) return "bar-high";
    if (rating >= 50) return "bar-mid";
    if (rating >= 40) return "bar-average";
    return "bar-low";
  }

  const p = stats[0];
  const mostPlayed = averages?.most_played_champions
    ? Object.entries(averages.most_played_champions)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name]) => name)
    : [];
  const avgRating = stats.length ? stats.reduce((sum, s) => sum + s.rating, 0) / stats.length : null;
  const groupedTrophies = trophies.reduce((acc, t) => {
    const key = t.tournament_name;
    if (!acc[key]) acc[key] = { ...t, count: 0 };
    acc[key].count += 1;
    return acc;
  }, {});

  if (loading) return <Loader loadingTitle={"Loading Player Stats"} showLogo={true} />;
  if (!stats.length)
    return (
      <div className="no-stats">
        <Topbar leagueId={leagueId} showLinks={true} showBudget={true} />
        <div style={{ display: "flex", alignItems: "center", flexDirection: "column", marginTop: "4rem" }}>
          <h3 style={{ textAlign: "center", color: "white", margin: "6px" }}>No stats available for this player yet</h3>
          <button onClick={() => navigate(`/dashboard/league/${leagueId}`)} className="edit-btn" style={{ width: "fit-content" }}>
            Return Home
          </button>
        </div>
      </div>
    );

  return (
    <div className="page-container" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Topbar leagueId={leagueId} showLinks={true} showBudget={true} />
      <div className="main">
        <div className="player-stats-page">
          <div className="player-banner">
            <div className="player-banner-left">
              <div className="player-avatar" style={{height: "160px", width: "160px", borderRadius: "10%"}}>
                <img src={`/players/${p.player_image}`} alt={p.player_name} />
              </div>
              <div className="player-profile-info">
                <div>
                  <h1>{p.player_name}</h1>
                </div>
                <div className="player-meta">
                  <span className="meta-item">
                    <img src={`/roles/${p.role}.svg`} alt={p.role} style={{ width: "18px", height: "18px", objectFit: "contain" }} />
                    |
                    <img src={`/teams/${p.team_logo}`} alt={p.team_name} className="meta-team-logo" />
                  </span>
                </div>
                <div onClick={() => navigate(`/league/${leagueId}/market`)} className="edit-btn" style={{ marginTop: "20px" }}>
                  View in market
                </div>
              </div>
            </div>

            <div className="player-banner-right"></div>
          </div>
          {trophies.length > 0 && (
            <div className="trophy-showcase">
              <div className="tpw-version">ACHIEVEMENTS</div>
              <div className="trophy-list">
                {Object.values(groupedTrophies).map((t) => (
                  <div className="trophy-item" key={t.tournament_name}>
                    <div className="trophy-img-wrap">
                      <img src={`/banners/${t.trophy_image}`} alt={t.tournament_name} className="trophy-img" />
                      {t.count > 1 && <span className="trophy-count">x{t.count}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {averages && (
            <div className="player-averages">
              {avgRating && (
                <div className="avg-stat">
                  <div className="tpw-version">SEASON RATING 1.0</div>
                  <span
                    className={`avg-value badge ${getBadgeClass(Math.round(avgRating))}`}
                    style={{ color: "black", width: "auto", padding: "2px 12px" }}>
                    {avgRating.toFixed(0)}
                  </span>
                </div>
              )}
              <div className="avg-stat">
                <div className="tpw-version">SEASON AVG KDA</div>
                <span className="avg-value">{Number(averages.avg_kda).toFixed(2)}</span>
              </div>
              {mostPlayed.length > 0 && (
                <div className="avg-stat">
                  <div className="tpw-version">SEASON TOP PLAYED</div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                    {mostPlayed.map((champ) => (
                      <div
                        key={champ}
                        className="avg-value champ-display"
                        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                        <div
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "4px",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}>
                          <img
                            src={`/champion/${champ}.png`}
                            alt={champ}
                            style={{
                              width: "120%",
                              height: "120%",
                              objectFit: "cover",
                              marginLeft: "-10%",
                              marginTop: "-10%",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="performances-chart-container">
            <span className="avg-label">Performances</span>
            <div className="performances-chart">
              {Object.entries(grouped).map(([matchId, { date, games }]) => (
                <div className="date-group" key={matchId}>
                  <div className="date-label">{date}</div>
                  <div className="bars">
                    {games.map((s, i) => {
                      const MIN = 30;
                      const heightPct = ((s.rating - MIN) / (100 - MIN)) * 100;
                      return (
                        <div
                          className="bar-col"
                          key={i}
                          //   onClick={() => navigate(`/dashboard/league/${leagueId}/history/${s.match_id}`)}
                          //   style={{ cursor: "pointer" }}
                        >
                          <div className="bar-wrap">
                            <span className={`badge ${getBadgeClass(s.rating)}`}>{s.rating}</span>
                            <div className={`bar ${getBarColor(s.rating)}`} style={{ height: `${heightPct}%` }} />
                          </div>
                          <div className="bar-meta">
                            <span className={`result-dot ${s.result === "W" ? "win" : "loss"}`}>{s.result}</span>
                            <img className="opp-logo" src={`/teams/${s.opponent_team_logo}`} alt={s.opponent_team_name} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="game-log">
            <span className="avg-label">Game Log</span>

            {Object.entries(grouped)
              .sort(([, a], [, b]) => new Date(b.games[0].start_time) - new Date(a.games[0].start_time))
              .map(([matchId, { date, games }]) => (
                <div className="gl-match-group" key={matchId}>
                  <div className="gl-match-header">
                    <span className="gl-match-date">{date}</span>
                    <div className="gl-match-meta">
                      <img src={`/banners/${games[0].tournament_banner}`} alt={games[0].tournament_name} className="gl-tournament-banner" />
                      <span className="gl-tournament-name">{games[0].tournament_name}</span>
                      <span className="gl-divider">·</span>
                      <span className="gl-match-format">BO{games.length > 3 ? 5 : 3}</span>
                    </div>
                  </div>

                  <div className="gl-games">
                    {games.map((s, i) => (
                      <div className={`gl-row ${s.result === "W" ? "gl-row-win" : "gl-row-loss"}`} key={i}>
                        <div className="gl-row-scroll">
                          {/* Champion */}
                          <div className="gl-champ">
                            <div className="gl-champ-img">
                              <img src={`/champion/${s.champion_played}.png`} alt={s.champion_played} />
                            </div>
                            <div className="gl-champ-info">
                              <span className="gl-champ-name">{s.champion_played}</span>
                              <span className="gl-game-num">Game {s.game_number}</span>
                            </div>
                          </div>

                          {/* Result + Opponent */}
                          <div className="gl-matchup">
                            <span className={`gl-result-word ${s.result === "W" ? "gl-win" : "gl-loss"}`}>
                              {s.result === "W" ? "Victory" : "Defeat"}
                            </span>
                            <div className="gl-opponent">
                              <span className="gl-vs">vs</span>
                              <img src={`/teams/${s.opponent_team_logo}`} alt={s.opponent_team_name} className="gl-opp-logo" />
                              <span className="gl-opp-name">{s.opponent_team_name}</span>
                            </div>
                          </div>

                          {/* KDA */}
                          <div className="gl-kda-block">
                            <span className="gl-kda-line">
                              <span className="gl-k">{s.kills}</span>
                              <span className="gl-kda-sep"> / </span>
                              <span className="gl-d">{s.deaths}</span>
                              <span className="gl-kda-sep"> / </span>
                              <span className="gl-a">{s.assists}</span>
                            </span>
                            <span className="gl-kda-ratio">{Number(s.kda).toFixed(2)} KDA</span>
                          </div>

                          {/* Stats */}
                          <div className="gl-stats">
                            <div className="gl-stat-item">
                              <span className="gl-stat-val">{s.damage.toLocaleString()}</span>
                              <span className="gl-stat-label">DMG</span>
                            </div>
                            <div className="gl-stat-item">
                              <span className="gl-stat-val">{s.cs}</span>
                              <span className="gl-stat-label">CS</span>
                            </div>
                            <div className="gl-stat-item">
                              <span className="gl-stat-val">{s.gpm}</span>
                              <span className="gl-stat-label">GPM</span>
                            </div>
                            <div className="gl-stat-item">
                              <span className="gl-stat-val">{s.dpm}</span>
                              <span className="gl-stat-label">DPM</span>
                            </div>
                          </div>
                        </div>
                        {/* Rating badge */}
                        <div className="gl-rating">
                          <span className={`badge ${getBadgeClass(s.rating)}`}>{s.rating}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
